import React, { useEffect, useState } from 'react';

import PortalLayout from '../PortalLayout';
import { usePortalReferrals } from '../../../services/portal';
import { TILE, StageBadge, SkeletonRow, EmptyState, ErrorState, Pager, fmtDate } from '../portalShared';

// The partner's referrals with reward-centric stages. Adaptive: a real table on
// desktop, tap-friendly cards on mobile. Link/QR signups arrive pre-masked from
// the server ("New client via your link · <month>").
const PortalReferrals = () => {
  const [page, setPage] = useState(1);
  const { data, isLoading, isError, isFetching, refetch } = usePortalReferrals(page);

  const rows = data?.rows || [];
  const total = data?.total || 0;
  const limit = data?.limit || 25;
  const totalPages = Math.max(1, Math.ceil(total / limit));

  // A shrinking list can strand the user on an empty later page — snap back.
  useEffect(() => {
    if (!isLoading && page > 1 && rows.length === 0 && total > 0) {
      setPage(Math.min(page - 1, totalPages));
    }
  }, [isLoading, page, rows.length, total, totalPages]);

  return (
    <PortalLayout>
      <div className="mb-4">
        <h1 className="text-xl font-bold text-white">Referrals</h1>
        <p className="mt-0.5 text-sm text-neutral-400">
          Everyone you&apos;ve sent our way — you earn $250 when a referral becomes a client and
          completes their claim.
        </p>
      </div>

      {isLoading ? (
        <div className="space-y-2.5">
          <SkeletonRow />
          <SkeletonRow />
          <SkeletonRow />
        </div>
      ) : isError ? (
        <ErrorState onRetry={refetch} />
      ) : rows.length === 0 ? (
        <EmptyState>No referrals yet — share your link or QR code to get started.</EmptyState>
      ) : (
        <>
          {/* Mobile: cards */}
          <div className="space-y-2.5 md:hidden">
            {rows.map((r) => (
              <div key={r.id} className={`${TILE} flex items-center justify-between px-4 py-3.5`}>
                <div className="min-w-0 pr-3">
                  <div className="truncate text-sm font-medium text-white">
                    {r.type === 'form' ? r.name : r.label}
                  </div>
                  <div className="mt-0.5 text-xs text-neutral-400">{fmtDate(r.occurredAt)}</div>
                </div>
                <StageBadge stage={r.stage} />
              </div>
            ))}
          </div>

          {/* Desktop: table */}
          <div className={`${TILE} hidden overflow-hidden md:block`}>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/10 text-left text-xs font-medium uppercase tracking-wide text-neutral-400">
                  <th className="px-5 py-3">Referral</th>
                  <th className="px-5 py-3">Date</th>
                  <th className="px-5 py-3 text-center">Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="border-b border-white/5 last:border-0">
                    <td className="px-5 py-3.5 font-medium text-white">
                      {r.type === 'form' ? r.name : r.label}
                    </td>
                    <td className="px-5 py-3.5 text-neutral-400">{fmtDate(r.occurredAt)}</td>
                    <td className="px-5 py-3.5 text-center">
                      <StageBadge stage={r.stage} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <Pager page={page} totalPages={totalPages} onPage={setPage} isFetching={isFetching} />
        </>
      )}
    </PortalLayout>
  );
};

export default PortalReferrals;
